import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { AlertService } from '../services/alert.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private inj = inject(Injector);
  private alertService = inject(AlertService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.handleRequest(req.clone({ withCredentials: true }), next);
  }

  private handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        const error = err as HttpErrorResponse | undefined;

        if (!error || typeof error.status !== 'number') {
          this.alertService.addSelfClosing({
            message: 'Network communication error, please reload and try again',
            id: 0,
            type: 'danger',
          });
          return throwError(() => err);
        }

        const errorMessage: string | undefined =
          typeof error.error === 'object' &&
          error.error !== null &&
          'message' in error.error &&
          typeof (error.error as { message?: unknown }).message === 'string'
            ? (error.error as { message?: string }).message
            : undefined;
        const requestUrl = error.url ?? request.url;

        if (error.status === 401) {
          if (requestUrl.endsWith('/login')) {
            console.log('Received 401 on login request');
          } else if (this.shouldRefreshSession(request, requestUrl)) {
            return this.refreshAndRetry(request, next);
          } else if (this.isProtectedApiRequest(requestUrl) && !this.isRefreshRequest(requestUrl)) {
            console.log('User credentials no longer valid for api call, reroute to login screen');
            this.inj.get(AuthenticationService).removeLocalSession();
            void this.router.navigate(['login'], { queryParams: { returnUrl: this.router.url } });
            return EMPTY;
          }
        }
        else if (error.status === 400) {
          if (errorMessage) {
            this.alertService.addSelfClosing({
              message: errorMessage,
              id: 0,
              type: errorMessage === 'The user has not been enrolled in Okta.' ? 'warning' : 'danger',
            });
          } else {
            this.alertService.addSticky({
              message: 'Unknown validation error occurred',
              id: 0,
              type: 'danger',
            });
          }
        }
        else if (error.status === 404) {
          if (errorMessage === 'The resource does not exist under normal business process') {
            console.log('Passing through assumed missing resource 404 normal business process error');
          } else {
            console.log('Ignoring 404 error for missing resource');
          }
          return throwError(() => error);
        }
        else if (error.status === 500) {
          if (error.url && error.url.endsWith('/login')) {
            console.log('Received 500 on login request');
            this.alertService.addSelfClosing({
              message: 'Invalid login, please try again',
              id: 0,
              type: 'warning',
            });
          } else if (errorMessage) {
            this.alertService.addSelfClosing({
              message: errorMessage,
              id: 0,
              type: 'danger',
            });
          }
        }
        else {
          this.alertService.addSelfClosing({
            message: 'Network communication error, please reload and try again',
            id: 0,
            type: 'danger',
          });
        }

        return throwError(() => error);
      })
    );
  }

  private shouldRefreshSession(request: HttpRequest<unknown>, requestUrl: string): boolean {
    return this.isProtectedApiRequest(requestUrl)
      && !this.isRefreshRequest(requestUrl)
      && !request.headers.has('x-auth-retry')
      && !request.headers.has('X-Auth-Retry');
  }

  private refreshAndRetry(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.inj.get(AuthenticationService).refreshAccessToken().pipe(
      catchError(() => {
        console.log('Session refresh failed, reroute to login screen');
        this.inj.get(AuthenticationService).removeLocalSession();
        void this.router.navigate(['login'], { queryParams: { returnUrl: this.router.url } });
        return EMPTY;
      }),
      switchMap(() => this.handleRequest(
        request.clone({
          withCredentials: true,
          setHeaders: {
            'x-auth-retry': 'true',
          },
        }),
        next
      ))
    );
  }

  private isProtectedApiRequest(requestUrl: string): boolean {
    return requestUrl.includes('/api/');
  }

  private isRefreshRequest(requestUrl: string): boolean {
    return requestUrl.includes('/api/auth/refresh');
  }
}
